import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min';

type ControlType = 'slider' | 'checkbox' | 'color' | 'select';

interface ControlConfig {
  name: string;
  folder?: string;
  type: ControlType;
  min?: number;
  max?: number;
  step?: number;
  options?: Record<string, any>;
  initialValue: any;
  onChange?: (value: any) => void;
}

interface GuiFolder {
  folder: GUI;
  controls: Map<string, any>;
}

/**
 * A singleton class to manage the GUI controls across the application
 */
class GuiManager {
  private static instance: GuiManager;
  private gui: GUI;
  private folders: Map<string, GuiFolder> = new Map();
  private rootControls: Map<string, any> = new Map();
  private visible: boolean = true;

  private constructor() {
    this.gui = new GUI();
    // Add GUI to the DOM but hide it initially
    this.gui.domElement.style.position = 'absolute';
    this.gui.domElement.style.top = '0';
    this.gui.domElement.style.right = '0';
  }

  /**
   * Get the singleton instance of GuiManager
   */
  public static getInstance(): GuiManager {
    if (!GuiManager.instance) {
      GuiManager.instance = new GuiManager();
    }
    return GuiManager.instance;
  }

  /**
   * Add a control to the GUI
   * @param config - Configuration for the control
   * @returns The controller object
   */
  public addControl(config: ControlConfig): any {
    const { name, folder, type, min, max, step, options, initialValue, onChange } = config;

    const targetObject = { [name]: initialValue };

    let controller;

    // Determine if we're adding to root or a folder
    if (folder) {
      // Get or create folder
      let folderGui: GuiFolder;
      if (!this.folders.has(folder)) {
        folderGui = {
          folder: this.gui.addFolder(folder),
          controls: new Map(),
        };
        this.folders.set(folder, folderGui);
      } else {
        folderGui = this.folders.get(folder)!;
      }

      // Add control to folder
      controller = this.createController(
        folderGui.folder,
        targetObject,
        name,
        type,
        min,
        max,
        step,
        options
      );
      folderGui.controls.set(name, controller);
    } else {
      // Add control to root
      controller = this.createController(
        this.gui,
        targetObject,
        name,
        type,
        min,
        max,
        step,
        options
      );
      this.rootControls.set(name, controller);
    }

    // Add onChange listener
    if (onChange) {
      controller.onChange((value: any) => {
        onChange(value);
      });
    }

    return controller;
  }

  /**
   * Update a control's value
   * @param name - The name of the control
   * @param value - The new value
   * @param folder - Optional folder name if the control is in a folder
   */
  public updateControl(name: string, value: any, folder?: string): void {
    let controller;

    if (folder) {
      const folderGui = this.folders.get(folder);
      if (!folderGui) {
        console.warn(`Folder ${folder} not found`);
        return;
      }
      controller = folderGui.controls.get(name);
    } else {
      controller = this.rootControls.get(name);
    }

    if (!controller) {
      console.warn(`Control ${name} not found`);
      return;
    }

    controller.setValue(value);
    controller.updateDisplay();
  }

  /**
   * Get a control's value
   * @param name - The name of the control
   * @param folder - Optional folder name if the control is in a folder
   * @returns The current value of the control
   */
  public getControlValue(name: string, folder?: string): any {
    let controller;

    if (folder) {
      const folderGui = this.folders.get(folder);
      if (!folderGui) {
        console.warn(`Folder ${folder} not found`);
        return undefined;
      }
      controller = folderGui.controls.get(name);
    } else {
      controller = this.rootControls.get(name);
    }

    if (!controller) {
      console.warn(`Control ${name} not found`);
      return undefined;
    }

    return controller.getValue();
  }

  /**
   * Create a controller based on its type
   */
  private createController(
    gui: GUI,
    obj: Record<string, any>,
    prop: string,
    type: ControlType,
    min?: number,
    max?: number,
    step?: number,
    options?: Record<string, any>
  ): any {
    switch (type) {
      case 'slider':
        return gui.add(obj, prop, min || 0, max || 1, step || 0.01);
      case 'checkbox':
        return gui.add(obj, prop);
      case 'color':
        return gui.addColor(obj, prop);
      case 'select':
        return gui.add(obj, prop, options || {});
      default:
        return gui.add(obj, prop);
    }
  }

  /**
   * Toggle GUI visibility
   */
  public toggleVisibility(): void {
    this.visible = !this.visible;
    this.gui.domElement.style.display = this.visible ? 'block' : 'none';
  }

  /**
   * Show the GUI
   */
  public show(): void {
    this.visible = true;
    this.gui.domElement.style.display = 'block';
  }

  /**
   * Hide the GUI
   */
  public hide(): void {
    this.visible = false;
    this.gui.domElement.style.display = 'none';
  }

  /**
   * Clear all controls
   */
  public clear(): void {
    this.gui.destroy();
    this.gui = new GUI();
    this.folders.clear();
    this.rootControls.clear();
  }
}

export default GuiManager;
